import { cn } from '@amore/ui/cn'
import { Badge } from '@amore/ui/components/badge'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
} from '@amore/ui/components/combobox'
import { Field } from '@amore/ui/components/field'
import { FormLayout } from '@amore/ui/components/form-layout'
import { Input } from '@amore/ui/components/input'
import { InputAddon, InputGroup } from '@amore/ui/components/input-group'
import { Textarea } from '@amore/ui/components/textarea'
import { Clock3, NotebookPen, Trash2, Users } from 'lucide-react'
import { useRef, useState } from 'react'
import { newId } from '@/lib/format'
import { usePrototype } from '@/mock/store'
import type { CeremonyStep } from '@/mock/types'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { ceremonyPeople, personKey } from './step-model'

const softControl = 'h-10 border-transparent bg-muted/60 hover:bg-muted'

export function CeremonyStepSheet({
  step,
  initialTitle = '',
  onClose,
}: {
  step?: CeremonyStep
  initialTitle?: string
  onClose: () => void
}) {
  const { data, canEdit, update } = usePrototype()
  const { showToast } = useToast()
  const [title, setTitle] = useState(step?.title ?? initialTitle)
  const [duration, setDuration] = useState(step?.durationMinutes?.toString() ?? '')
  const [people, setPeople] = useState(step?.people ?? [])
  const [description, setDescription] = useState(step?.description ?? '')
  const [query, setQuery] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [errors, setErrors] = useState<{ title?: string; duration?: string }>({})
  const titleRef = useRef<HTMLTextAreaElement>(null)
  const durationRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const options = ceremonyPeople(data)
  const selected = new Set(people.map(personKey))
  const needle = query
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('cs')
  const available = options.filter(
    (option) =>
      !selected.has(personKey(option.person)) &&
      `${option.name} ${option.category}`
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLocaleLowerCase('cs')
        .includes(needle),
  )
  const dirty =
    canEdit &&
    (title !== (step?.title ?? initialTitle) ||
      duration !== (step?.durationMinutes?.toString() ?? '') ||
      description !== (step?.description ?? '') ||
      people.map(personKey).join('|') !== (step?.people ?? []).map(personKey).join('|'))

  const save = () => {
    if (!canEdit) return
    const nextErrors = {
      ...(!title.trim() ? { title: 'Doplňte název bodu.' } : {}),
      ...(duration.trim() &&
      (!/^\d+$/.test(duration.trim()) ||
        Number(duration) < 1 ||
        !Number.isSafeInteger(Number(duration)))
        ? { duration: 'Zadejte kladný počet celých minut, nebo nechte pole prázdné.' }
        : {}),
    }
    setErrors(nextErrors)
    if (nextErrors.title) return titleRef.current?.focus()
    if (nextErrors.duration) return durationRef.current?.focus()
    const saved: CeremonyStep = {
      id: step?.id ?? newId('ceremony-step'),
      title: title.trim(),
      durationMinutes: duration.trim() ? Number(duration) : null,
      people,
      description: description.trim(),
    }
    update((current) => ({
      ...current,
      ceremony: {
        ...current.ceremony,
        steps: step
          ? current.ceremony.steps.map((item) => (item.id === step.id ? saved : item))
          : [...current.ceremony.steps, saved],
      },
    }))
    showToast({
      title: step ? 'Bod uložen' : 'Bod přidán',
      description: saved.title,
      tone: 'success',
    })
    onClose()
  }

  const remove = () => {
    if (!canEdit || !step) return
    update((current) => ({
      ...current,
      ceremony: {
        ...current.ceremony,
        steps: current.ceremony.steps.filter((item) => item.id !== step.id),
      },
    }))
    showToast({ title: 'Bod smazán', description: step.title })
    onClose()
  }

  return (
    <>
      <DetailSheet
        open
        eyebrow="Scénář obřadu"
        title={step?.title || 'Nový bod'}
        dirty={dirty}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        titleContent={
          <div className="space-y-1">
            <textarea
              ref={titleRef}
              value={title}
              rows={1}
              maxLength={150}
              readOnly={!canEdit}
              data-autofocus={(!step && canEdit) || undefined}
              aria-label="Název bodu"
              aria-invalid={Boolean(errors.title)}
              aria-describedby={errors.title ? 'ceremony-step-title-error' : undefined}
              placeholder="Název bodu"
              onChange={(event) => {
                setTitle(event.target.value)
                setErrors((current) => ({ ...current, title: undefined }))
              }}
              className={cn(
                'field-sizing-content -mx-2 w-[calc(100%+1rem)] resize-none rounded-lg bg-transparent px-2 py-1 font-heading text-[1.75rem] leading-tight tracking-tight outline-none transition-colors',
                canEdit && 'hover:bg-muted/60 focus:bg-muted/60',
              )}
            />
            {errors.title ? (
              <p
                id="ceremony-step-title-error"
                role="alert"
                className="text-sm text-danger-foreground"
              >
                {errors.title}
              </p>
            ) : null}
          </div>
        }
        destructiveAction={
          canEdit && step
            ? {
                label: 'Smazat bod',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmDelete(true),
              }
            : undefined
        }
        primaryAction={
          canEdit ? { label: 'Uložit', onClick: () => formRef.current?.requestSubmit() } : undefined
        }
      >
        <FormLayout
          ref={formRef}
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            save()
          }}
          className="divide-y divide-border space-y-0 [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1"
        >
          <Field
            controlId="ceremony-step-duration"
            error={errors.duration}
            label={
              <span className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
                <Clock3 className="size-4 shrink-0" aria-hidden="true" />
                Délka<span className="sr-only"> v minutách</span>
              </span>
            }
          >
            <InputGroup className="max-w-40">
              <Input
                ref={durationRef}
                inputMode="numeric"
                value={duration}
                readOnly={!canEdit}
                placeholder="Bez délky"
                className={softControl}
                onChange={(event) => {
                  setDuration(event.target.value)
                  setErrors((current) => ({ ...current, duration: undefined }))
                }}
              />
              <InputAddon side="end">min</InputAddon>
            </InputGroup>
          </Field>
          <section className="space-y-3" aria-labelledby="ceremony-step-people-heading">
            <h3
              id="ceremony-step-people-heading"
              className="flex items-center gap-2 text-sm font-normal text-muted-foreground"
            >
              <Users className="size-4 shrink-0" aria-hidden="true" />
              Zapojení lidé
            </h3>
            {people.length ? (
              <div className="flex flex-wrap gap-2">
                {people.map((person) => {
                  const key = personKey(person)
                  const name =
                    options.find((option) => personKey(option.person) === key)?.name ??
                    'Dříve vybraný člověk'
                  return canEdit ? (
                    <Badge
                      key={key}
                      tone="neutral"
                      removeLabel={`Odebrat ${name}`}
                      onRemove={() =>
                        setPeople((current) => current.filter((item) => personKey(item) !== key))
                      }
                    >
                      {name}
                    </Badge>
                  ) : (
                    <Badge key={key} tone="neutral">
                      {name}
                    </Badge>
                  )
                })}
              </div>
            ) : !canEdit ? (
              <p className="text-sm text-muted-foreground">Nikdo není vybraný</p>
            ) : null}
            {canEdit ? (
              <Combobox
                value=""
                query={query}
                onQueryChange={setQuery}
                onValueChange={(value) => {
                  const option = options.find((option) => personKey(option.person) === value)
                  if (option)
                    setPeople((current) =>
                      current.some((person) => personKey(person) === value)
                        ? current
                        : [...current, option.person],
                    )
                }}
              >
                <ComboboxInput
                  aria-label="Přidat člověka"
                  placeholder="Přidat člověka…"
                  className={softControl}
                />
                <ComboboxContent>
                  <ComboboxList>
                    {available.length ? (
                      available.map((option) => (
                        <ComboboxOption
                          key={personKey(option.person)}
                          value={personKey(option.person)}
                        >
                          <span className="block">{option.name}</span>
                          <span className="block text-xs text-muted-foreground">
                            {option.category}
                          </span>
                        </ComboboxOption>
                      ))
                    ) : (
                      <ComboboxEmpty>Nikdo další neodpovídá hledání.</ComboboxEmpty>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            ) : null}
          </section>
          <Field
            controlId="ceremony-step-instructions"
            label={
              <span className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
                <NotebookPen className="size-4 shrink-0" aria-hidden="true" />
                Pokyny
              </span>
            }
          >
            <Textarea
              value={description}
              readOnly={!canEdit}
              rows={5}
              maxLength={3000}
              placeholder={canEdit ? 'Co se má stát a kdo co udělá…' : 'Bez pokynů'}
              onChange={(event) => setDescription(event.target.value)}
              className="min-h-24 border-transparent bg-muted/60 hover:bg-muted focus:bg-card"
            />
          </Field>
        </FormLayout>
      </DetailSheet>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        heading="Smazat bod?"
        description={`Bod „${step?.title ?? ''}“ se odebere ze scénáře.`}
        keepLabel="Ponechat bod"
        confirmLabel="Smazat bod"
        onConfirm={remove}
      />
    </>
  )
}
